<script setup lang="ts">
import { computed } from 'vue'
import { NXGDSButton, NXGDSButtonSizes, NXGDSButtonVariants } from '@nsftx/seven-nxg-design-system'
import {
  useDmCoreSpaceOperatorAuthStore,
  useDmCoreSpaceOperatorStore
} from '@nsftx/seven-client-retail-vue-dm-core'
import { storeToRefs } from 'pinia'
import { logService } from '@nsftx/seven-client-retail-utils'
import errorParser from '@/utils/errorParser'
import { redirectToSsbt } from '@/utils/ssbtRedirect'
import { useCmsConfigStore } from '@/stores/cms/cmsConfigStore'
const cmsSettings = useCmsConfigStore()
const logoSrc = computed(() => cmsSettings.cmsConfig?.appSettings.logo ?? null)

const LOG_PREFIX = '[SBOHeader]'

const { logoutOperator, clearOperatorSession } = useDmCoreSpaceOperatorAuthStore()
const { operatorDisplayName } = storeToRefs(useDmCoreSpaceOperatorStore())

const handleLogOut = async () => {
  try {
    await logoutOperator()
  } catch (error) {
    logService.error(`${LOG_PREFIX} Operator logout failed.`, {
      code: 'SBO_OPERATOR_LOGOUT_ERROR',
      ...errorParser.parseUpstream(error)
    })
    clearOperatorSession()
  }

  redirectToSsbt()
}
</script>

<template>
  <header class="sbo-header st-flex-center-between">
    <div class="sbo-header__left-content">
      <RouterLink class="sbo-header__left-content__logo-wrapper" to="/">
        <img v-if="logoSrc" class="sbo-header__left-content__logo" :src="logoSrc" />
      </RouterLink>
      <div class="sbo-header__separator">|</div>
      <div class="sbo-header__title">{{ $t('backoffice_title') }}</div>
    </div>
    <div class="sbo-header__right-content">
      <div>
        <div class="sbo-header__right-content__operator-label">
          {{ $t('backoffice_header_operator_label') }}
        </div>
        <div>{{ operatorDisplayName }}</div>
      </div>
      <div class="sbo-header__separator">|</div>
      <div>
        <NXGDSButton
          :variant="NXGDSButtonVariants.invertedBrand"
          :size="NXGDSButtonSizes.huge"
          @click="handleLogOut"
        >
          {{ $t('backoffice_logout_button') }}
        </NXGDSButton>
      </div>
    </div>
  </header>
</template>
<style scoped lang="scss">
.sbo-header {
  padding: 0 var(--nxgds-alias-spacing-xs) 0 var(--nxgds-alias-spacing-lg);
  font-family: var(--nxgds-alias-type-terminal-label-regular-font-family);
  background: var(--nxgds-mapped-color-surface-brand);
  color: var(--nxgds-mapped-color-text-on-brand-primary);

  &__separator {
    color: var(--nxgds-mapped-color-text-on-brand-disabled);
  }

  &__title {
    color: var(--nxgds-mapped-color-text-on-brand-primary);
    font-family: var(--nxgds-alias-type-terminal-h3-regular-font-family);
    font-size: var(--nxgds-alias-type-terminal-h3-regular-size);
    font-style: normal;
    font-weight: var(--nxgds-alias-type-terminal-h3-regular-weight);
    line-height: var(--nxgds-alias-type-terminal-h3-regular-line-height);
    letter-spacing: var(--nxgds-alias-type-terminal-h3-regular-letter-spacing);
  }

  &__left-content,
  &__right-content {
    display: flex;
    align-items: center;
  }

  &__right-content {
    text-align: right;
    gap: var(--nxgds-alias-spacing-md);

    &__operator-label {
      color: var(--nxgds-mapped-color-text-on-brand-secondary);
    }
  }

  &__left-content {
    gap: var(--nxgds-alias-spacing-lg);

    &__logo-wrapper {
      height: 48px;
      max-width: 180px;
    }

    &__logo {
      height: 100%;
    }
  }
}
</style>
